
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { formSchema } from "../schema";
import { MembershipFormData } from "../types";
import { useState, useRef } from "react";
import { prepareApplicationData, submitApplicationData } from "../utils/formSubmissionService";
import { sendConfirmationEmails } from "../utils/emailService";
import { resetFormToInitialState } from "../utils/formHelpers";

export const useMembershipForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSendingIssue, setEmailSendingIssue] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const captchaWidgetId = useRef<string | null>(null);
  
  const form = useForm<MembershipFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      applicationDate: new Date(),
      termsAccepted: false,
      captchaToken: "",
    },
  });

  const onCaptchaVerify = (token: string) => {
    form.setValue("captchaToken", token);
  };

  const onCaptchaExpire = () => {
    form.setValue("captchaToken", "");
    toast({
      title: "Verification Expired",
      description: "Please complete the verification again before submitting.",
      variant: "destructive",
    });
  };
  
  const onCaptchaWidgetId = (widgetId: string) => {
    captchaWidgetId.current = widgetId;
  };

  const onSubmit = async (values: MembershipFormData) => {
    setIsSubmitting(true);
    setEmailSendingIssue(false);
    setSubmissionError(null);
    
    try {
      // Verify captchaToken exists
      if (!values.captchaToken) {
        throw new Error("Please complete the verification challenge");
      }

      // Log submission attempt
      console.log("Attempting to submit application form:", {
        business_name: values.businessName,
        email: values.email,
        timestamp: new Date().toISOString()
      });
      
      // Prepare application data for submission
      const applicationData = prepareApplicationData(values);
      
      // Submit the application data
      const submissionResult = await submitApplicationData(applicationData);
      
      if (!submissionResult.success) {
        throw new Error(submissionResult.error);
      }

      const applicationId = submissionResult.applicationId!;

      // Send email notification after successful database insert
      // Include all necessary fields for PDF generation
      const emailResult = await sendConfirmationEmails({
        id: applicationId,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        business_name: values.businessName,
        membership_type: values.membershipType,
        application_date: applicationData.application_date,
        business_address_street1: values.businessAddress.street1,
        business_address_street2: values.businessAddress.street2,
        business_address_city: values.businessAddress.city,
        business_address_postal_code: values.businessAddress.postalCode,
        home_address_street1: values.address.street1,
        home_address_street2: values.address.street2,
        home_address_city: values.address.city,
        home_address_postal_code: values.address.postalCode,
        mobile_area_code: values.mobileNumber.areaCode,
        mobile_number: values.mobileNumber.number,
        business_area_code: values.businessNumber.areaCode,
        business_number: values.businessNumber.number,
        current_potato_area: values.currentPotatoArea?.toString(),
        total_potato_area: values.totalPotatoArea?.toString(),
        planned_potato_area: values.plannedPotatoArea?.toString(),
        signature: values.signature
      });

      if (!emailResult.success || emailResult.emailSendingIssue) {
        setEmailSendingIssue(true);
        toast({
          title: "Application submitted",
          description: "Your application was submitted successfully, but there may have been an issue sending the confirmation email.",
        });
      } else {
        const pdfMessage = emailResult.pdfAttached 
          ? " A PDF copy has been attached to the email."
          : "";
        
        toast({
          title: "Application submitted successfully!",
          description: `We'll review your application and get back to you soon. A confirmation has been sent to your email.${pdfMessage}`,
        });
      }

      // Reset the form with initial values
      resetFormToInitialState(form);

      // Try to reset the Turnstile after successful submission
      // We use a try-catch to ensure that Turnstile errors don't affect the user experience
      try {
        if (window.turnstile && captchaWidgetId.current && document.getElementById('membership-form')) {
          console.log("Attempting to reset Turnstile widget:", captchaWidgetId.current);
          window.turnstile.reset(captchaWidgetId.current);
        } else {
          // This is a non-critical operation, just log it
          console.log("Skipping Turnstile reset: widget ID not found or form no longer in DOM");
        }
      } catch (turnstileError) {
        // Don't let Turnstile errors affect the form submission success
        console.warn("Non-critical error resetting Turnstile:", turnstileError);
      }

    } catch (error: any) {
      console.error("Error submitting application:", error);
      const errorMessage = error?.message || "There was an error submitting your application. Please try again later.";
      setSubmissionError(errorMessage);
      toast({
        title: "Error submitting application",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    emailSendingIssue,
    submissionError,
    onCaptchaVerify,
    onCaptchaExpire,
    onCaptchaWidgetId
  };
};
